import AppContainer from 'app_components/layouts/app'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'

import './CertificateSettings.scss'
import CertificateSettingsFormModal from './components/CertificateSettingsFormModal/CertificateSettingsFormModal'
import CourseSchedulingDetailsService from 'app_services/admin/courseScheduling/courseSchedulingDetailsService';
import { ICourseSchedulingDetailListItem } from 'app_types/admin/courseScheduling/courseSchedulingDetailsTypes';
import { useParams } from 'react-router-dom';
import ButtonComponent from 'app_components/commons/Button/ButtonComponent';
import { IoIosAddCircleOutline } from 'react-icons/io';
import { ICertificateSettings } from 'app_types/admin/certificateSettings/certificateSettings.types';
import CertificateSettingsService from 'app_services/admin/certificateSettings/certificateSettingsService';
import CertificateSettingsCard from './components/CertificateSettingsCard/CertificateSettingsCard';
import { ScreenNotificationsUtil } from 'app_utils/screenNotification';
import AlertComponent from 'app_components/commons/alert';
import BreadcrumbComponent from 'app_components/commons/breadcrumb';
import { useTranslation } from 'react-i18next';
import { ICourseSchedulingListItem } from 'app_types/admin/courseScheduling/courseSchedulingTypes';
import CourseSchedulingService from 'app_services/admin/courseScheduling/courseSchedulingService';
import UserService from 'app_services/user/userService';

const PAGE_LIMIT = 1000

const CertificateSettings: FC = () => {
  const { courseSchedulingId } = useParams<{ courseSchedulingId: string }>()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [editData, setEditData] = useState<ICertificateSettings>()
  const [courseSchedulingDetails, setCourseSchedulingDetails] = useState<ICourseSchedulingDetailListItem[]>([])
  const [certificateSettings, setCertificateSettings] = useState<ICertificateSettings[]>([])
  const [courseScheduling, setCourseScheduling] = useState<ICourseSchedulingListItem>()
  const courseSchedulingDetailsService = useMemo(() => new CourseSchedulingDetailsService(), [])
  const certificateSettingsService = useMemo(() => new CertificateSettingsService(), [])
  const courseSchedulingService = useMemo(() => new CourseSchedulingService(), [])
  const screenNotification = useMemo(() => new ScreenNotificationsUtil(), [])
  const userService = useMemo(() => new UserService(), [])
  const { t } = useTranslation()

  const getCourseSchedulingDetails = useCallback(async () => {
    const response = await courseSchedulingDetailsService.list({
      course_scheduling: courseSchedulingId,
      pageNumber: 1,
      nPerPage: PAGE_LIMIT,
    })
    if (response?.status_code === 'success' && response?.schedulings?.length) {
      setCourseSchedulingDetails(response.schedulings)
    } else {
      setCourseSchedulingDetails([])
    }
  }, [courseSchedulingDetailsService, courseSchedulingId])

  const getCertificateSettings = useCallback(async () => {
    const response = await certificateSettingsService.list({
      nPerPage: String(PAGE_LIMIT),
      pageNumber: '1',
      courseScheduling: courseSchedulingId,
    })
    if (response?.code === 200 && response?.certificateSettings?.length) {
      setCertificateSettings(response.certificateSettings)
    } else {
      setCertificateSettings([])
    }
  }, [certificateSettingsService, courseSchedulingId])

  const getCourseScheduling = useCallback(async () => {
    const response = await courseSchedulingService.getOne(courseSchedulingId)
    if (response) {
      setCourseScheduling(response)
    }
  }, [courseSchedulingService, courseSchedulingId])

  useEffect(() => {
    getCourseSchedulingDetails()
  }, [getCourseSchedulingDetails])

  useEffect(() => {
    getCertificateSettings()
  }, [getCertificateSettings])

  useEffect(() => {
    getCourseScheduling()
  }, [getCourseScheduling])

  const handleCloseModal = useCallback((status?: boolean) => {
    setIsOpen(false)
    setEditData(undefined)
    if (status) {
      getCertificateSettings()
    }
  }, [getCertificateSettings])

  const handleClickAddCertificate = () => {
    setIsOpen(true)
  }

  const handleDeleteCertificateSetting = useCallback(async (certificateSettings: ICertificateSettings) => {
    const confirm = await screenNotification.showAskDelete()
    if (confirm?.isConfirmed) {
      const response = await certificateSettingsService.delete({ id: certificateSettings?._id })
      if (response?.code === 200) {
        screenNotification.showSuccessTimer()
        getCertificateSettings()
      }
    }
  }, [screenNotification, certificateSettingsService, getCertificateSettings])

  const handleEditCertificateSetting = (certificateSettings: ICertificateSettings) => {
    setEditData(certificateSettings)
    setIsOpen(true)
  }

  return (
    <AppContainer>
      <div className="certificate-settings--layout">
        <BreadcrumbComponent
          links={[
            '/admin/course-scheduling',
            `/admin/course-scheduling/${courseSchedulingId}`,
            '#',
          ]}
          texts={[
            `${t('components.breadcrumb.course_scheduling')}`,
            `${t('components.breadcrumb.course_scheduling_details')}`,
            'Certificados'
          ]}
        />
        <div>
          <h3>Administración de certificados: {courseScheduling?.program?.name}</h3>
          <h4>ID de Servicio: {courseScheduling?.metadata?.service_id}</h4>
          <h4>Cliente: {courseScheduling?.client?.name}</h4>
        </div>
        {userService.can('permission:course_scheduling_certificate_multiple_configuration', 'module:course_scheduling') && (
          <div className="certificate-settings--header">
            <ButtonComponent onClick={handleClickAddCertificate} variant='primary'>
              <ButtonComponent.Icon>
                <IoIosAddCircleOutline />
              </ButtonComponent.Icon>
              Agregar certificado
            </ButtonComponent>
          </div>
        )}
        <div className="certificate-settings--list">
          {certificateSettings?.length ?
            certificateSettings?.map((certificate) => (
              <CertificateSettingsCard
                key={certificate?._id}
                certificateSettings={certificate}
                onDelete={() => handleDeleteCertificateSetting(certificate)}
                onEdit={() => handleEditCertificateSetting(certificate)}
                courseSchedulingDetails={courseSchedulingDetails}
                disableActions={!courseScheduling?.multipleCertificate?.editingStatus}
              />
            ))
          : (
            <AlertComponent description="No hay certificados para mostrar" type='info' />
          )}
        </div>
        {courseScheduling && (
          <CertificateSettingsFormModal
            onClose={handleCloseModal}
            open={isOpen}
            courseSchedulingDetails={courseSchedulingDetails}
            editData={editData}
            courseScheduling={courseScheduling}
          />
        )}
      </div>
    </AppContainer>
  )
}

export default CertificateSettings
